/* eslint-disable import/no-unresolved */
import React, { ReactNode } from 'react';
import DashboardIcon from '@mui/icons-material/Dashboard';
import InputIcon from '@mui/icons-material/Input';
import WorkIcon from '@mui/icons-material/Work';
import SettingsIcon from '@mui/icons-material/Settings';
import GroupIcon from '@mui/icons-material/Group';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CategoryIcon from '@mui/icons-material/Category';
import ListIcon from '@mui/icons-material/List';
import DataArrayIcon from '@mui/icons-material/DataArray';
import DescriptionIcon from '@mui/icons-material/Description';
import BusinessIcon from '@mui/icons-material/Business';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import TranslateIcon from '@mui/icons-material/Translate';
import ScienceIcon from '@mui/icons-material/Science';
import HistoryIcon from '@mui/icons-material/History';
import EmailIcon from '@mui/icons-material/Email';
import VaultIcon from '@mui/icons-material/AccountBalance';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import ArticleIcon from '@mui/icons-material/Article';
import DPOptions from '../pages/DPOptions';
import DataElements from '../pages/DataElements';
import Processors from '../pages/Processors';
import Purposes from '../pages/Purposes';
import Dashboard from '../pages/Dashboard';
import CollectionPoints from '../pages/CollectionPoints';
import Labels from '../pages/Labels/index';
import SandboxConsents from '../pages/Sandbox/SandboxConsents';
import SandboxConsentDetails from '../pages/Sandbox/SandboxConsentDetails';
import ProductionConsents from '../pages/ProductionConsents/ProductionConsents';
import ProductionConsentDetails from '../pages/ProductionConsents/ProductionConsentDetails';
import AuditLogs from '../pages/AuditLogs';
import Requests from '../pages/Requests';
import RequestDetails from '../pages/Requests/RequestDetails';
import DataPrincipals from '../pages/DataPrincipals';
import DataPrincipalDetails from '../pages/DataPrincipals/DataPrincipalDetails';
import Notices from '../pages/Notices';
import AdminLayout from '../pages/Admin/AdminLayout';
import AdminDashboardPage from '../pages/Admin/AdminDashboardPage';
import ClientListPage from '../pages/Clients/ClientListPage';
import ClientDetailPage from '../pages/Clients/ClientDetailPage';
import ApiDocsPage from '../pages/Documentation/ApiDocsPage';
import RequirePermission from 'components/auth/RequirePermission';
import UserManualLayout from 'pages/UserManual/UserManualLayout';
import UserManualOverviewPage from 'pages/UserManual/sections/UserManualOverviewPage';
import UserManualSetupPage from 'pages/UserManual/sections/UserManualSetupPage';
import UserManualWorkbenchPage from 'pages/UserManual/sections/UserManualWorkbenchPage';
import UserManualCollectionPointsPage from 'pages/UserManual/sections/UserManualCollectionPointsPage';
import UserManualDashboardPage from 'pages/UserManual/sections/UserManualDashboardPage';
import UserManualSettingsPage from 'pages/UserManual/sections/UserManualSettingsPage';
import UserManualNoticesPage from 'pages/UserManual/sections/UserManualNoticesPage';
import UserManualAuditLogsPage from 'pages/UserManual/sections/UserManualAuditLogsPage';
import UserManualRequestsPage from 'pages/UserManual/sections/UserManualRequestsPage';
import UserManualConsentsPage from 'pages/UserManual/sections/UserManualConsentsPage';
import UserManualDataPrincipalsPage from 'pages/UserManual/sections/UserManualDataPrincipalsPage';
import { USER_MANUAL_NAV_ITEMS } from './userManualNav';

export interface RouteConfig {
    id: string;
    label: string;
    path: string;
    icon?: ReactNode;
    children?: RouteConfig[];
    element?: ReactNode;
    hideFromMenu?: boolean;
    /** If set, this route is shown in the menu only when the user has this permission. */
    requiredPermission?: string;
}

const userManualChildUi: Record<string, { icon: ReactNode; element: ReactNode }> = {
    'user-manual-overview': {
        icon: React.createElement(ArticleIcon),
        element: React.createElement(UserManualOverviewPage)
    },
    'user-manual-setup': {
        icon: React.createElement(SettingsIcon),
        element: React.createElement(UserManualSetupPage)
    },
    'user-manual-workbench': {
        icon: React.createElement(WorkIcon),
        element: React.createElement(UserManualWorkbenchPage)
    },
    'user-manual-data-principals': {
        icon: React.createElement(GroupIcon),
        element: React.createElement(UserManualDataPrincipalsPage)
    },
    'user-manual-requests': {
        icon: React.createElement(AssignmentIcon),
        element: React.createElement(UserManualRequestsPage)
    },
    'user-manual-consents': {
        icon: React.createElement(AssignmentIcon),
        element: React.createElement(UserManualConsentsPage)
    },
    'user-manual-audit-logs': {
        icon: React.createElement(HistoryIcon),
        element: React.createElement(UserManualAuditLogsPage)
    },
    'user-manual-notices': {
        icon: React.createElement(DescriptionIcon),
        element: React.createElement(UserManualNoticesPage)
    },
    'user-manual-collection-points': {
        icon: React.createElement(InputIcon),
        element: React.createElement(UserManualCollectionPointsPage)
    },
    'user-manual-dashboard': {
        icon: React.createElement(DashboardIcon),
        element: React.createElement(UserManualDashboardPage)
    },
    'user-manual-settings': {
        icon: React.createElement(SettingsIcon),
        element: React.createElement(UserManualSettingsPage)
    }
};

const userManualRouteChildren: RouteConfig[] = USER_MANUAL_NAV_ITEMS.map((item) => {
    const ui = userManualChildUi[item.id];
    if (!ui) {
        throw new Error(`Missing user manual route UI for id: ${item.id}`);
    }
    return {
        ...item,
        icon: ui.icon,
        element: ui.element
    };
});

export const routes: RouteConfig[] = [
    {
        id: 'dashboard',
        label: 'Dashboard',
        path: '/dashboard',
        icon: React.createElement(DashboardIcon),
        element: React.createElement(Dashboard)
    },
    {
        id: 'workbench',
        label: 'Workbench',
        path: '/workbench',
        icon: React.createElement(WorkIcon),
        children: [
            {
                id: 'data-principals',
                label: 'Data Principals',
                path: '/workbench/data-principals',
                icon: React.createElement(GroupIcon),
                element: React.createElement(DataPrincipals)
            },
            {
                id: 'data-principal-details',
                label: 'Data Principal Details',
                path: '/workbench/data-principals/:dpId',
                icon: React.createElement(GroupIcon),
                element: React.createElement(DataPrincipalDetails),
                hideFromMenu: true
            },
            {
                id: 'requests',
                label: 'Requests',
                path: '/workbench/requests',
                icon: React.createElement(AssignmentIcon),
                element: React.createElement(Requests)
            },
            {
                id: 'request-details',
                label: 'Request Details',
                path: '/workbench/requests/:requestId',
                icon: React.createElement(AssignmentIcon),
                element: React.createElement(RequestDetails),
                hideFromMenu: true
            },
            {
                id: 'consents',
                label: 'Consents',
                path: '/workbench/consents',
                icon: React.createElement(AssignmentIcon),
                element: React.createElement(ProductionConsents)
            },
            {
                id: 'consent-details',
                label: 'Consent Details',
                path: '/workbench/consents/:consentId',
                icon: React.createElement(AssignmentIcon),
                element: React.createElement(ProductionConsentDetails),
                hideFromMenu: true
            },
            {
                id: 'audit-logs',
                label: 'Audit Logs',
                path: '/workbench/audit-logs',
                icon: React.createElement(HistoryIcon),
                element: React.createElement(AuditLogs)
            },
            {
                id: 'notices',
                label: 'Notices',
                path: '/workbench/notices',
                icon: React.createElement(DescriptionIcon),
                element: React.createElement(Notices)
            }
        ]
    },
    {
        id: 'setup',
        label: 'Setup',
        path: '/setup',
        icon: React.createElement(SettingsIcon),
        children: [
            {
                id: 'collection-points',
                label: 'Collection points',
                path: '/setup/collection-points',
                icon: React.createElement(InputIcon),
                element: React.createElement(CollectionPoints)
            },
            {
                id: 'purposes',
                label: 'Purposes',
                path: '/setup/purposes',
                icon: React.createElement(CategoryIcon),
                element: React.createElement(Purposes)
            },
            {
                id: 'dp-options',
                label: 'Preferences',
                path: '/setup/dp-options',
                icon: React.createElement(ListIcon),
                element: React.createElement(DPOptions)
            },
            {
                id: 'data-elements',
                label: 'Data Elements',
                path: '/setup/data-elements',
                icon: React.createElement(DataArrayIcon),
                element: React.createElement(DataElements)
            },
            {
                id: 'labels',
                label: 'Labels',
                path: '/setup/labels',
                icon: React.createElement(TranslateIcon),
                element: React.createElement(Labels)
            },
            {
                id: 'processors',
                label: 'Processors',
                path: '/setup/processors',
                icon: React.createElement(BusinessIcon),
                element: React.createElement(Processors)
            }
        ]
    },
    {
        id: 'sandbox',
        label: 'Sandbox',
        path: '/sandbox',
        icon: React.createElement(ScienceIcon),
        children: [
            {
                id: 'sandbox-consents',
                label: 'Consents',
                path: '/sandbox/consents',
                icon: React.createElement(AssignmentIcon),
                element: React.createElement(SandboxConsents)
            },
            {
                id: 'sandbox-consent-details',
                label: 'Consent Details',
                path: '/sandbox/consents/:consentId',
                icon: React.createElement(AssignmentIcon),
                element: React.createElement(SandboxConsentDetails),
                hideFromMenu: true
            }
        ]
    },
    {
        id: 'user-manual',
        label: 'User manual',
        path: '/user-manual',
        icon: React.createElement(MenuBookIcon),
        hideFromMenu: true,
        element: React.createElement(UserManualLayout),
        children: userManualRouteChildren
    },
    {
        id: 'api-docs',
        label: 'API docs',
        path: '/api-docs',
        icon: React.createElement(IntegrationInstructionsIcon),
        hideFromMenu: true,
        requiredPermission: 'MANAGE_CLIENT',
        element: React.createElement(
            RequirePermission,
            { permission: 'MANAGE_CLIENT' },
            React.createElement(ApiDocsPage)
        )
    },
    {
        id: 'admin',
        label: 'Admin',
        path: '/admin',
        icon: React.createElement(AdminPanelSettingsIcon),
        element: React.createElement(AdminLayout),
        requiredPermission: 'MANAGE_CLIENT',
        children: [
            {
                id: 'admin-dashboard',
                label: 'Dashboard',
                path: '/admin/dashboard',
                icon: React.createElement(DashboardIcon),
                element: React.createElement(AdminDashboardPage)
            },
            {
                id: 'admin-clients',
                label: 'Clients',
                path: '/admin/clients',
                icon: React.createElement(GroupIcon),
                element: React.createElement(ClientListPage)
            },
            {
                id: 'admin-client-detail',
                label: 'Client detail',
                path: '/admin/clients/:clientId',
                icon: React.createElement(GroupIcon),
                element: React.createElement(ClientDetailPage),
                hideFromMenu: true
            }
        ]
    }
];